import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  ArrowUpRight,
  BarChart3,
  Boxes,
  CircleDollarSign,
  Gauge,
  Leaf,
  PackageOpen,
  ShieldCheck,
  TrendingUp,
  WalletCards,
} from 'lucide-react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { summarizeInsights } from '../lib/insights';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const compactNumber = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

function readableError(error) {
  const code = error?.code || '';
  if (code.includes('permission-denied')) return 'Firestore blocked your insights data. Confirm the published rules allow your user subcollections.';
  if (code.includes('unavailable')) return 'MM could not reach Firebase. Check your connection and try again.';
  return error?.message || 'Insights could not be loaded.';
}

export default function Insights({ user, onRequestAuth, onNavigate }) {
  const [estimates, setEstimates] = useState([]);
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(Boolean(user));
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user || !db) {
      setEstimates([]);
      setProducts([]);
      setMaterials([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    let firstEstimateSnapshot = true;
    const onError = (snapshotError) => { setError(readableError(snapshotError)); setLoading(false); };
    const stops = [
      onSnapshot(collection(db, 'users', user.uid, 'estimates'), (snapshot) => {
        setEstimates(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        if (firstEstimateSnapshot) { firstEstimateSnapshot = false; setLoading(false); }
      }, onError),
      onSnapshot(collection(db, 'users', user.uid, 'products'), (snapshot) => setProducts(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))), onError),
      onSnapshot(collection(db, 'users', user.uid, 'materials'), (snapshot) => setMaterials(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))), onError),
    ];
    return () => stops.forEach((stop) => stop());
  }, [user]);

  const summary = useMemo(() => summarizeInsights({ estimates, products, materials }), [estimates, materials, products]);
  const hasWorkspaceData = summary.estimateCount || summary.productCount || summary.materialCount;

  if (!user) {
    return (
      <section className="signed-out-state brushed-panel">
        <span className="signed-out-state__icon"><BarChart3 size={30} /></span><p className="eyebrow">Private business insights</p><h1>Sign in to see the business behind each print.</h1><p>MM turns saved estimates, reusable products, and filament inventory into a live profitability view.</p>
        <div className="signed-out-state__actions"><button className="button button--primary button--large" type="button" onClick={() => onRequestAuth('signup')}>Create account</button><button className="button button--ghost button--large" type="button" onClick={() => onRequestAuth('signin')}>Sign in</button></div>
      </section>
    );
  }

  return (
    <section className="profiles-page feature-page insights-page">
      <header className="profiles-page__hero"><div><p className="eyebrow">Business insights · Live</p><h1>See where the margin is going.</h1><p>Every saved estimate contributes to a clear view of quoted revenue, projected profit, production cost, and inventory readiness.</p></div><button className="button button--primary button--large" type="button" onClick={() => onNavigate('calculator')}><WalletCards size={19} /> New estimate</button></header>

      {error && <div className="config-banner config-banner--error" role="alert"><ShieldCheck size={18} /><span>{error}</span><button type="button" onClick={() => setError('')}>Dismiss</button></div>}

      <section className="insights-kpis" aria-label="Business summary">
        <article><span><CircleDollarSign size={20} /></span><div><small>Quoted revenue</small><strong>{currency.format(summary.revenue)}</strong><p>Across {summary.estimateCount} saved estimate{summary.estimateCount === 1 ? '' : 's'}</p></div></article>
        <article><span><TrendingUp size={20} /></span><div><small>Projected profit</small><strong>{currency.format(summary.profit)}</strong><p>{currency.format(summary.averageProfit)} average per saved batch</p></div></article>
        <article><span><Gauge size={20} /></span><div><small>Average target margin</small><strong>{compactNumber.format(summary.averageMargin)}%</strong><p>{currency.format(summary.productionCost)} production cost represented</p></div></article>
        <article><span><Leaf size={20} /></span><div><small>Filament remaining</small><strong>{(summary.inventoryRemainingGrams / 1000).toFixed(2)} kg</strong><p>{summary.lowStockCount} low-stock spool{summary.lowStockCount === 1 ? '' : 's'}</p></div></article>
      </section>

      {loading ? (
        <section className="feature-library brushed-panel"><div className="profiles-loading" aria-live="polite"><span /><span /><span /><p>Building your insights…</p></div></section>
      ) : !hasWorkspaceData ? (
        <section className="feature-library brushed-panel"><div className="profiles-empty"><span><Activity size={28} /></span><h3>Your insights will build as MM learns your workflow.</h3><p>Save an estimate, add a catalog product, or track a material spool to activate this dashboard.</p><button className="button button--primary" type="button" onClick={() => onNavigate('calculator')}>Create first estimate</button></div></section>
      ) : (
        <div className="insights-layout">
          <section className="insights-panel brushed-panel insights-panel--wide">
            <div className="section-heading"><div><p className="eyebrow">Cost composition</p><h2>What saved jobs cost</h2></div><span>{currency.format(summary.productionCost)} total</span></div>
            {summary.estimateCount ? <div className="insight-bars">{summary.costBreakdown.map((item) => <div className="insight-bar" key={item.id}><span><strong>{item.label}</strong><small>{currency.format(item.value)}</small></span><div><i style={{ width: `${item.percentOfLargest}%` }} /></div></div>)}</div> : <div className="insights-empty-inline">Save an estimate to unlock cost composition.</div>}
          </section>

          <section className="insights-panel metal-panel">
            <div className="section-heading"><div><p className="eyebrow">Top opportunity</p><h2>Highest projected profit</h2></div><TrendingUp size={18} /></div>
            {summary.topEstimates.length ? <div className="insight-rankings">{summary.topEstimates.map((estimate, index) => <button type="button" key={estimate.id} onClick={() => onNavigate('calculator', { estimateId: estimate.id })}><span>{index + 1}</span><div><strong>{estimate.name || 'Untitled estimate'}</strong><small>{estimate.profileName || 'Saved profile'}</small></div><b>{currency.format(Number(estimate.results?.totalProfit || 0))}</b><ArrowUpRight size={14} /></button>)}</div> : <div className="insights-empty-inline">No saved estimate profits yet.</div>}
          </section>

          <section className="insights-panel brushed-panel">
            <div className="section-heading"><div><p className="eyebrow">Workspace coverage</p><h2>V1 foundations</h2></div><Boxes size={18} /></div>
            <div className="coverage-grid"><button type="button" onClick={() => onNavigate('estimates')}><WalletCards size={18} /><span><strong>{summary.estimateCount}</strong><small>Saved estimates</small></span></button><button type="button" onClick={() => onNavigate('catalog')}><PackageOpen size={18} /><span><strong>{summary.productCount}</strong><small>Catalog products</small></span></button><button type="button" onClick={() => onNavigate('materials')}><Leaf size={18} /><span><strong>{summary.materialCount}</strong><small>Tracked spools</small></span></button></div>
          </section>

          <section className="insights-panel brushed-panel insights-panel--wide">
            <div className="section-heading"><div><p className="eyebrow">Inventory health</p><h2>Material readiness</h2></div><span>{currency.format(summary.inventoryValue)} remaining value</span></div>
            <div className="inventory-health"><div><span className={summary.lowStockCount ? 'health-dot health-dot--warning' : 'health-dot'} /><div><strong>{summary.lowStockCount ? `${summary.lowStockCount} spool${summary.lowStockCount === 1 ? '' : 's'} need attention` : 'Stock levels look healthy'}</strong><small>Low-stock thresholds come from the Materials workspace.</small></div></div><button className="button button--ghost" type="button" onClick={() => onNavigate('materials')}>Open materials <ArrowUpRight size={15} /></button></div>
          </section>
        </div>
      )}
    </section>
  );
}
